import type { ServerDetail, ServerStatus, ServerSummary, SseEventName } from '@comficonf/shared';
import { SSE_EVENTS } from '@comficonf/shared';
import type { QueryClient } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useSyncExternalStore } from 'react';
import { api } from './instance';
import { queryKeys } from './keys';

type Listener = () => void;

/**
 * Counts `server.resources` notifications per resource, so an open editor learns that its resource changed
 * outside the editor.
 */
class ResourceChangeSignals {
  private readonly counts = new Map<string, number>();
  private readonly listeners = new Set<Listener>();

  private static key(serverId: string, resourceId: string): string {
    return `${serverId}\u0000${resourceId}`;
  }

  notify(serverId: string, resourceIds: readonly string[]): void {
    for (const resourceId of resourceIds) {
      const key = ResourceChangeSignals.key(serverId, resourceId);
      this.counts.set(key, (this.counts.get(key) ?? 0) + 1);
    }
    for (const listener of this.listeners) listener();
  }

  get(serverId: string, resourceId: string): number {
    return this.counts.get(ResourceChangeSignals.key(serverId, resourceId)) ?? 0;
  }

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };
}

export const resourceChangeSignals = new ResourceChangeSignals();

/** Number of external change notifications received for a resource during this session. */
export function useResourceChangeSignal(serverId: string, resourceId: string): number {
  return useSyncExternalStore(
    resourceChangeSignals.subscribe,
    () => resourceChangeSignals.get(serverId, resourceId),
    () => 0,
  );
}

export interface ServerEventHandlers {
  onServerRemoved?: (serverId: string, displayName: string | undefined) => void;
}

function setStatus(queryClient: QueryClient, serverId: string, status: ServerStatus): void {
  queryClient.setQueryData<ServerSummary[]>(queryKeys.servers, (servers) =>
    servers?.map((server) => (server.id === serverId ? { ...server, status } : server)),
  );
  queryClient.setQueryData<ServerDetail>(queryKeys.server(serverId), (server) =>
    server ? { ...server, status } : server,
  );
}

/** Applies one server-sent event to the query cache. Returns false for unknown or malformed events. */
export function applyServerEvent(
  queryClient: QueryClient,
  name: string,
  data: string,
  handlers: ServerEventHandlers = {},
): boolean {
  if (!(name in SSE_EVENTS)) return false;
  let json: unknown;
  try {
    json = JSON.parse(data);
  } catch {
    return false;
  }
  switch (name as SseEventName) {
    case 'server.status': {
      const parsed = SSE_EVENTS['server.status'].safeParse(json);
      if (!parsed.success) return false;
      const { serverId, status } = parsed.data;
      setStatus(queryClient, serverId, status);
      void queryClient.invalidateQueries({ queryKey: queryKeys.servers, exact: true });
      void queryClient.invalidateQueries({ queryKey: queryKeys.server(serverId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.plugins(serverId) });
      if (status === 'online') {
        void queryClient.invalidateQueries({ queryKey: queryKeys.resources(serverId) });
      }
      return true;
    }
    case 'server.resources': {
      const parsed = SSE_EVENTS['server.resources'].safeParse(json);
      if (!parsed.success) return false;
      const { serverId, changed } = parsed.data;
      void queryClient.invalidateQueries({ queryKey: queryKeys.plugins(serverId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.servers, exact: true });
      void queryClient.invalidateQueries({ queryKey: queryKeys.server(serverId) });
      for (const resourceId of changed) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.resource(serverId, resourceId) });
      }
      if (changed.length > 0) resourceChangeSignals.notify(serverId, changed);
      return true;
    }
    case 'server.removed': {
      const parsed = SSE_EVENTS['server.removed'].safeParse(json);
      if (!parsed.success) return false;
      const { serverId } = parsed.data;
      const name =
        queryClient.getQueryData<ServerDetail>(queryKeys.server(serverId))?.displayName ??
        queryClient.getQueryData<ServerSummary[]>(queryKeys.servers)?.find((s) => s.id === serverId)
          ?.displayName;
      queryClient.setQueryData<ServerSummary[]>(queryKeys.servers, (servers) =>
        servers?.filter((server) => server.id !== serverId),
      );
      queryClient.removeQueries({ queryKey: queryKeys.serverScope(serverId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.servers, exact: true });
      handlers.onServerRemoved?.(serverId, name);
      return true;
    }
  }
}

const RECONNECT_BASE_MS = 2_000;
const RECONNECT_MAX_MS = 60_000;

/**
 * Subscribes to GET /events while `enabled`, keeping server queries in sync. EventSource retries transient
 * errors itself; when it gives up, the hook reconnects with exponential backoff. After a reconnect, server
 * queries are refetched to catch up with missed events.
 */
export function useServerEvents(enabled: boolean, handlers: ServerEventHandlers = {}): void {
  const queryClient = useQueryClient();
  const { onServerRemoved } = handlers;

  useEffect(() => {
    if (!enabled || typeof EventSource === 'undefined') return;
    let source: EventSource | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let failures = 0;
    let interrupted = false;
    let disposed = false;

    const listeners = Object.keys(SSE_EVENTS).map((name) => {
      const listener = (event: MessageEvent<string>) => {
        applyServerEvent(queryClient, name, event.data, { onServerRemoved });
      };
      return [name, listener] as const;
    });

    const connect = () => {
      if (disposed) return;
      source = new EventSource(api.eventsUrl(), { withCredentials: true });
      for (const [name, listener] of listeners) source.addEventListener(name, listener);
      source.addEventListener('open', () => {
        failures = 0;
        if (interrupted) {
          interrupted = false;
          void queryClient.invalidateQueries({ queryKey: queryKeys.servers });
          void queryClient.invalidateQueries({ queryKey: ['server'] });
        }
      });
      source.addEventListener('error', () => {
        interrupted = true;
        if (source?.readyState !== EventSource.CLOSED) return;
        source = null;
        failures += 1;
        const delay = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** (failures - 1));
        timer = setTimeout(connect, delay * (0.5 + Math.random() * 0.5));
      });
    };

    connect();
    return () => {
      disposed = true;
      if (timer) clearTimeout(timer);
      source?.close();
    };
  }, [enabled, queryClient, onServerRemoved]);
}
